import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  createProject,
  getEngagementCategories,
  type CreateProjectInput,
  type CreateProjectResult,
  type EngagementCategoryOption,
  type RecordClass,
} from '../api.js';
import { useChrome } from '../chrome.js';
import { BackLink } from '../components/BackLink.js';
import { useSession } from '../useSession.js';
import { canCreateProject } from '../roles.js';

/**
 * The three classes a person can open, with the copy that tells them which one they mean.
 *
 * ⚠️ ALL THREE, NOT THE TWO THAT ARE CONVENIENT. `planIntake` accepts exactly `analyzed_deal`,
 * `pursuit`, and `ggp_project`; a picker offering fewer would make a legitimate record unrepresentable
 * through the only door canon allows, which is the "field narrower than reality" failure one level up
 * — the record class would be missing from the UI rather than wrong in the database.
 */
const CLASSES: { value: RecordClass; label: string; help: string }[] = [
  {
    value: 'pursuit',
    label: 'Pursuit',
    help: 'A deal GGP is chasing. It carries no project number — one is minted only when the IC promotes it.',
  },
  {
    value: 'analyzed_deal',
    label: 'Analyzed deal',
    help: 'A client’s deal we are underwriting. It is the client’s record and never carries a GGP project number.',
  },
  {
    value: 'ggp_project',
    label: 'GGP project (migrating an existing number)',
    help: 'A project that already has its GGP number — used to bring an existing record in. Owners and admins only.',
  },
];

export interface IntakeFormState {
  readonly recordClass: RecordClass;
  readonly projectName: string;
  readonly projectCode: string;
  readonly sponsorName: string;
  readonly sponsorContext: string;
  readonly engagementCategory: string;
  readonly leadOwnerName: string;
}

/** What the form sends to `POST /projects`. Pure, so the body is tested rather than rendered. */
export function intakeBody(f: IntakeFormState): CreateProjectInput {
  return {
    recordClass: f.recordClass,
    projectName: f.projectName.trim(),
    // Only ever sent for the one class that may carry it — never as an empty string, which the
    // server would have to interpret.
    projectCode: f.recordClass === 'ggp_project' ? f.projectCode.trim() : null,
    sponsorName: f.sponsorName.trim() || null,
    sponsorContext: f.sponsorContext.trim() || null,
    // Empty means "Something else" — sent as null so the SERVER applies Chad's fallback rather than
    // this form deciding what an unanswered category means.
    engagementCategory: f.engagementCategory || null,
    leadOwnerName: f.leadOwnerName.trim() || null,
  };
}

/**
 * INTAKE — the door a record comes in through (ADR-0035 §7.1, canon R-a: "project creation is
 * human-initiated through the PEE only; no auto-create").
 *
 * ⚠️ THIS ROUTE EXISTED ON THE SERVER AND NOWHERE ELSE. `POST /projects` has been authed and live
 * since P1, and nothing in the SPA called it — so by ADR-0035's own bar ("each phase ends in something
 * a pilot user can do") the intake gate ended in nothing anyone could do. The projects list said so
 * out loud, pointing readers at an HTTP verb.
 */
export function NewProject() {
  const navigate = useNavigate();
  const session = useSession();
  // Supplied by the application root — `AppHeader` in the Deal Analyzer, `HubHeader` in the Hub.
  // This page is rendered by both and no longer decides which product it belongs to (see chrome.tsx).
  const Chrome = useChrome();
  const [recordClass, setRecordClass] = useState<RecordClass>('pursuit');
  const [projectName, setProjectName] = useState('');
  const [projectCode, setProjectCode] = useState('');
  const [sponsorName, setSponsorName] = useState('');
  const [sponsorContext, setSponsorContext] = useState('');
  /*
    The Lead Owner, prefilled with the signed-in person's own name (the session's signed `name`) until
    they type over it. Null means untouched, so the prefill follows the session rather than freezing an
    empty string from before the session loaded.
  */
  const [leadOwnerEdit, setLeadOwnerEdit] = useState<string | null>(null);
  /*
    The engagement category, which decides the trailing token of the PERMANENT job number. Empty means
    "Something else", which is Chad's own documented fallback rather than an absence we invented.
  */
  const [engagementCategory, setEngagementCategory] = useState('');
  const [categories, setCategories] = useState<EngagementCategoryOption[]>([]);
  const [fallbackTypeCode, setFallbackTypeCode] = useState('Proj');
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState<CreateProjectResult | null>(null);

  /*
    Chad's vocabulary comes from the server, never from a copy in this bundle — see
    `getEngagementCategories`. ⚠️ ABOVE THE EARLY RETURNS ON PURPOSE: hooks cannot sit after the
    `session.status` guard below, and moving it down would be a conditional hook rather than a tidier
    read. It degrades to an empty list, which leaves only "Something else" and still creates records.
  */
  useEffect(() => {
    let live = true;
    void getEngagementCategories().then((res) => {
      if (!live) return;
      setCategories(res.categories);
      setFallbackTypeCode(res.fallbackTypeCode);
    });
    return () => {
      live = false;
    };
  }, []);

  if (session.status !== 'authed') {
    return <div className="app__loading">Loading…</div>;
  }

  /*
    🔴 HIDING THE BUTTONS IS WORTHLESS WITHOUT THIS. `/projects/new` is a URL: it is reachable from a
    bookmark, a shared link, browser history, or the address bar, and none of those pass through the
    list page that stopped offering it. A guard on the entry points only is the same "some of its
    entry points" mistake `#768` fixed on the API — counting the doors is the whole job.

    Sent to the project list rather than shown a refusal: they arrived at a page for an act their
    role does not perform, and the list is the thing they actually came to see.
  */
  if (!canCreateProject(session.claims)) return <Navigate to="/projects" replace />;

  const signedName = session.claims['name'];
  const leadOwnerName = leadOwnerEdit ?? (typeof signedName === 'string' ? signedName : '');

  // R-e, mirrored in the form rather than only enforced after submit: a number belongs to exactly one
  // class. Asking for it on a pursuit would invite a refusal the reader could have been spared.
  const wantsCode = recordClass === 'ggp_project';
  const selected = CLASSES.find((c) => c.value === recordClass);
  const canSubmit =
    projectName.trim() !== '' && (!wantsCode || projectCode.trim() !== '') && !submitting;

  /*
    The job number this intake will mint, shown before it becomes permanent.

    ⚠️ `getUTCFullYear`, NOT `getFullYear` — the server mints on the UTC year, with its own comment
    saying a local year "would mint two 0001s". A form showing the local year would disagree with the
    number actually minted for anyone west of UTC on New Year's Eve, and would do it on the one field
    that can never be corrected.

    ⚠️ The SEQUENCE is deliberately not guessed. It is allocated inside the intake transaction and
    nothing here can know it, so it renders as `####` — a plausible-looking number would be a
    fabrication on a permanent key.
  */
  const year = new Date().getUTCFullYear();
  const typeCode =
    categories.find((c) => c.value === engagementCategory)?.typeCode ?? fallbackTypeCode;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setOutcome(null);
    const res = await createProject(
      intakeBody({
        recordClass,
        projectName,
        projectCode,
        sponsorName,
        sponsorContext,
        engagementCategory,
        leadOwnerName,
      }),
    );
    setSubmitting(false);
    setOutcome(res);
    if (res.status === 'created') {
      // A pursuit has no code and `/projects/:code` cannot address it, so the list is the honest
      // destination. A numbered record goes straight to its own record.
      void navigate(
        res.project.code ? `/projects/${encodeURIComponent(res.project.code)}` : '/projects',
      );
    }
  }

  return (
    <div className="app">
      {Chrome !== null && <Chrome claims={session.claims} />}
      <main className="app__main">
        <div className="app__crumbs">
          <BackLink fallback="/projects" label="All projects" />
        </div>

        <div className="app__intro">
          <h1>Open a record</h1>
          <p className="sub">
            A project record is created deliberately, by a named person — that is what makes it a
            record. The intake is kept with it, so who opened this and when stays a question with an
            answer.
          </p>
        </div>

        <form className="df" onSubmit={(e) => void handleSubmit(e)}>
          <fieldset className="df__group">
            <legend>What kind of record is this?</legend>
            {CLASSES.map((c) => (
              <label className="df__check df__check--stacked" key={c.value}>
                {/* A radio takes no `df__inputwrap` — that wrapper styles a full-width text control. */}
                <input
                  type="radio"
                  name="recordClass"
                  value={c.value}
                  checked={recordClass === c.value}
                  onChange={() => setRecordClass(c.value)}
                />
                {/*
                  `df__checkbody` stacks the label above its help text. Without it `.df__hint` is
                  inline and renders as "PursuitA deal GGP is chasing." — see the note in styles.css.
                */}
                <span className="df__checkbody">
                  <strong>{c.label}</strong>
                  <span className="df__hint">{c.help}</span>
                </span>
              </label>
            ))}
          </fieldset>

          <label className="df__field df__field--full">
            <span className="df__label">Project name</span>
            <span className="df__inputwrap">
              <input
                type="text"
                maxLength={200}
                required
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="The Commons"
              />
            </span>
            <span className="df__hint">It is how anyone finds this again.</span>
          </label>

          {/*
            THE ENGAGEMENT CATEGORY — it decides the trailing token of the permanent job number.

            ⚠️ SHOWN WITH ITS CONSEQUENCE, because the consequence is irreversible. The number is
            minted at Established and never reissued, so a category left unanswered is not a blank to
            fill in later — it is `2026-0001-Proj`, permanently. The hint below states the token this
            choice will mint rather than leaving someone to find out after the record exists.

            The options are the server's, not a copy in this bundle: the vocabulary is Chad's and is
            his to extend. When the list fails to load only "Something else" is offered, which is the
            honest degradation — the server applies the same fallback either way.
          */}
          <label className="df__field df__field--full">
            <span className="df__label">Engagement category</span>
            <span className="df__inputwrap">
              <select
                value={engagementCategory}
                onChange={(e) => setEngagementCategory(e.target.value)}
              >
                <option value="">Something else</option>
                {categories.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.value}
                  </option>
                ))}
              </select>
            </span>
            <span className="df__hint">
              This record will be numbered <strong>{`${year}-####-${typeCode}`}</strong>. The number
              is minted now and never reissued, so the category cannot be corrected afterwards.
            </span>
          </label>

          {wantsCode && (
            <label className="df__field df__field--full">
              <span className="df__label">Existing GGP project number</span>
              <span className="df__inputwrap">
                <input
                  type="text"
                  maxLength={64}
                  required
                  value={projectCode}
                  onChange={(e) => setProjectCode(e.target.value)}
                  placeholder="2025-014"
                />
              </span>
              <span className="df__hint">
                Only for a record that already carries its number. To create a new one, open a
                pursuit and promote it — that is the decision that mints a number.
              </span>
            </label>
          )}

          <label className="df__field df__field--full">
            <span className="df__label">Lead Owner (optional)</span>
            <span className="df__inputwrap">
              <input
                type="text"
                maxLength={200}
                value={leadOwnerName}
                onChange={(e) => setLeadOwnerEdit(e.target.value)}
              />
            </span>
            <span className="df__hint">
              The person who owns any stop this project raises. It starts as your name; change it if
              someone else owns this project. Left blank, a stop opens with no owner.
            </span>
          </label>

          <label className="df__field df__field--full">
            <span className="df__label">Sponsor (optional)</span>
            <span className="df__inputwrap">
              <input
                type="text"
                maxLength={200}
                value={sponsorName}
                onChange={(e) => setSponsorName(e.target.value)}
                placeholder="Vance Development Partners"
              />
            </span>
          </label>

          <label className="df__field df__field--full">
            <span className="df__label">Sponsor track record (optional)</span>
            <span className="df__inputwrap">
              <textarea
                maxLength={8000}
                rows={4}
                value={sponsorContext}
                onChange={(e) => setSponsorContext(e.target.value)}
                placeholder="Named principals, prior deliveries, GC history…"
              />
            </span>
            {/*
              ⚠️ "As stated", deliberately. The engine GROUNDS the Sponsor & Execution module's evidence
              quote against this text, so anything typed here can be quoted back to a client as
              evidence. It must be what a person asserted, never something inferred from the deal's own
              documents.
            */}
            <span className="df__hint">
              What someone has stated about this sponsor. It is quoted as evidence, so write only
              what was actually said — every run of this project inherits it.
            </span>
          </label>

          {/*
            ⚠️ A REFUSAL IS A RECOMMENDATION (canon R7), so it renders as the server's own sentence.
            `planIntake` writes prose meant to be read by the person who tripped it — "a pursuit
            carries no project number — 2025-014 would be assigned before the IC decides" — and
            replacing that with "Bad Request" throws away the only part that says what to do next.
          */}
          {outcome && outcome.status !== 'created' && (
            <div className={`notice${outcome.status === 'error' ? ' error' : ''}`}>
              <strong>
                {outcome.status === 'refused'
                  ? 'That record cannot be opened as described.'
                  : outcome.status === 'forbidden'
                    ? 'You do not have permission for this.'
                    : outcome.status === 'conflict'
                      ? 'That project number is already in use.'
                      : 'Something went wrong.'}
              </strong>{' '}
              {outcome.reason}
            </div>
          )}

          <div className="df__bar">
            <button type="submit" disabled={!canSubmit}>
              {submitting ? 'Opening…' : `Open ${selected?.label.toLowerCase() ?? 'record'}`}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}
